<template>
  <DataCard
    title="Gestion des congés"
    icon="fas fa-calendar-alt"
    icon-color="text-purple-600"
    icon-bg-color="bg-purple-100"
    class="w-full"
  >
    <template #header-actions>
      <button
        @click="$emit('save')"
        :disabled="saving || disableSave"
        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <i v-if="saving" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-save"></i>
        <span>{{ saving ? "Sauvegarde..." : "Sauvegarder" }}</span>
      </button>
    </template>
    <div class="space-y-6">
      <!-- Ajouter un congé -->
      <div class="space-y-4">
        <h3 class="text-lg font-semibold text-black">Ajouter un congé</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <BaseInput
            v-model="newLeave.startDate"
            label="Début de congé"
            type="date"
            :min="minStartDate"
          />
          <BaseInput
            v-model="newLeave.endDate"
            label="Fin de congé"
            type="date"
            :min="minEndDate"
          />
          <div class="flex flex-col">
            <label class="block text-sm font-medium text-black mb-1">&nbsp;</label>
            <ActionButton
              :label="saving ? 'Ajout...' : 'Ajouter'"
              :icon="saving ? 'fas fa-spinner fa-spin' : 'fas fa-plus'"
              variant="primary"
              @click="addLeave"
              :disabled="!newLeave.startDate || !newLeave.endDate || saving"
            />
          </div>
        </div>
      </div>

      <!-- Onglets pour séparer congés actifs et supprimés -->
      <div class="space-y-4">
        <div class="border-b border-gray-200">
          <nav class="-mb-px flex space-x-8">
            <button
              @click="activeTab = 'active'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm',
                activeTab === 'active'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-black hover:text-black hover:border-gray-300'
              ]"
            >
              Congés actifs
              <span class="ml-2 bg-purple-100 text-purple-600 py-0.5 px-2 rounded-full text-xs">
                {{ activeLeaves.length }}
              </span>
            </button>
            <button
              @click="activeTab = 'deleted'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm',
                activeTab === 'deleted'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-black hover:text-black hover:border-gray-300'
              ]"
            >
              Anciens congés supprimés
              <span class="ml-2 bg-red-100 text-red-600 py-0.5 px-2 rounded-full text-xs">
                {{ deletedLeaves.length }}
              </span>
            </button>
          </nav>
        </div>

        <!-- Congés actifs -->
        <div v-if="activeTab === 'active'" class="space-y-4">
          <div class="overflow-x-auto">
            <table class="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">N°</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Début de congé</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Fin de congé</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="activeLeaves.length === 0">
                  <td colspan="4" class="px-4 py-8 text-center text-black">
                    <i class="fas fa-calendar-times text-4xl text-black mb-2 block"></i>
                    Aucun congé enregistré
                  </td>
                </tr>
                <tr v-for="(leave, index) in activeLeaves" :key="index">
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-black">{{ index + 1 }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-black">{{ formatDate(leave.startDate) }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-black">{{ formatDate(leave.endDate) }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      type="button"
                      @click="deleteLeave(formData.leaves.indexOf(leave))"
                      class="text-red-600 hover:text-red-900"
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Anciens congés supprimés -->
        <div v-if="activeTab === 'deleted'" class="space-y-4">
          <div class="overflow-x-auto">
            <table class="min-w-full bg-white border border-gray-200 rounded-lg">
              <thead class="bg-red-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">N°</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Début de congé</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Fin de congé</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">Statut</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="deletedLeaves.length === 0">
                  <td colspan="4" class="px-4 py-8 text-center text-black">
                    <i class="fas fa-trash text-4xl text-black mb-2 block"></i>
                    Aucun ancien congé supprimé
                  </td>
                </tr>
                <tr v-for="(leave, index) in deletedLeaves" :key="index" class="bg-red-50">
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-black">{{ index + 1 }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-black">{{ formatDate(leave.startDate) }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-black">{{ formatDate(leave.endDate) }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-red-600">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <i class="fas fa-trash mr-1"></i>
                      Supprimé
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </DataCard>
</template>

<script setup lang="ts">
import { DataCard, BaseInput, ActionButton } from '@/components'
import { reactive, ref, computed } from 'vue'
import type { GeneralInformationFormData } from '@/types'

const props = defineProps<{
  formData: GeneralInformationFormData
  saving?: boolean
  disableSave?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:formData', value: GeneralInformationFormData): void
  (e: 'save'): void
  (e: 'add-leave', value: GeneralInformationFormData): void
}>()

const newLeave = reactive<{
  startDate: string
  endDate: string
}>({
  startDate: '',
  endDate: ''
})

// État des onglets
const activeTab = ref<'active' | 'deleted'>('active')

// Computed properties pour séparer les congés
const activeLeaves = computed(() => 
  props.formData.leaves.filter(leave => !leave.deleted)
)

const deletedLeaves = computed(() => 
  props.formData.leaves.filter(leave => leave.deleted)
)

const addLeave = () => {
  if (newLeave.startDate && newLeave.endDate) {
    // Validations
    const startDate = new Date(newLeave.startDate)
    const endDate = new Date(newLeave.endDate)

    // 1) Le début de congé doit être au minimum le lendemain de la date du jour
    const today = new Date()
    const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
    if (startDate < tomorrow) {
      alert('Le début de congé doit commencer à partir de demain')
      return
    }

    // 2) La fin de congé doit être strictement après le début de congé
    if (endDate <= startDate) {
      alert('La fin de congé doit être postérieure au début de congé')
      return
    }
    
    const updatedFormData = {
      ...props.formData,
      leaves: [
        ...props.formData.leaves,
        { startDate: newLeave.startDate, endDate: newLeave.endDate }
      ]
    }
    // On ne met PAS à jour localement tout de suite (pas de mise à jour
    // optimiste) : on laisse la sauvegarde se faire, puis loadVendeurData()
    // rafraîchira la liste. Ainsi la nouvelle ligne et la notification
    // apparaissent en même temps (plus d'effet "la ligne apparaît puis le
    // popup arrive après").
    emit('add-leave', updatedFormData)
    
    // Réinitialiser les champs
    newLeave.startDate = ''
    newLeave.endDate = ''
  }
}

const deleteLeave = (index: number) => {
  // Marquer comme supprimé au lieu de retirer du tableau
  // La suppression en DB se fera au moment de la sauvegarde générale avec supprimer = 1
  const updatedFormData = { ...props.formData }
  updatedFormData.leaves[index].deleted = true
  emit('update:formData', updatedFormData)
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

// Helpers pour contraindre les dates des inputs de type date
const toInputDate = (date: Date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// Début de congé: à partir du lendemain de la date actuelle
const minStartDate = computed(() => {
  const now = new Date()
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
  return toInputDate(tomorrow)
})

// Fin de congé: au moins le lendemain du début de congé sélectionné
const minEndDate = computed(() => {
  if (!newLeave.startDate) return ''
  const s = new Date(newLeave.startDate)
  const next = new Date(s.getFullYear(), s.getMonth(), s.getDate() + 1)
  return toInputDate(next)
})
</script>
